import React from "react";
import withComponentLibraryTheme from "hocs/withComponentLibraryTheme";
import TextField from "@material-ui/core/TextField";
import { RadioGroup } from "@my-scoot/component-library-legacy";
import { SectionUnlockingSwitch } from "features/AdminPanel/modules/SectionUnlockingSwitch/SectionUnlockingSwitch";
import { AddCircleOutline, Close } from "@material-ui/icons";
import classnames from "classnames";
import { ExlyModal } from "features/Common/modules/ExlyModal/ExlyModal";
import ExlyImage from "features/Common/modules/ExlyImage/ExlyImage";
import { getS3AssetCDNUrl } from "features/Common/modules/File/modules/Upload/utils/FileUpload.utils";
import { UploadFileButton } from "features/ManageOfferings/modules/UploadFileButton/UploadFileButton";
import ExlyLoader from "ui/modules/ExlyLoader";
import { validURLV2 } from "utils/validations";
import { navigateTo } from "utils/urlUtils";
import { app_route_keys } from "constants/urlPaths";
import { FEATURE_SECTION_KEY } from "features/Account/modules/Features/constants/Features.constants";
import { getUploadedFiles } from "features/ManageOfferings/modules/ManageContractInputSubSection/utils/contractUpload.utils";
import { KYC_TYPE_OPTIONS } from "features/ManageOfferings/modules/OfferingPageSections/constants/OfferingPageSectionsKeys.constants";
import { ESIGN_PLACEMENT_OPTIONS } from "features/ManageOfferings/modules/OfferingPageSections/constants/OfferingPageSections.constants";
import { ESIGN_PLACEMENT } from "@my-scoot/exly-react-component-lib";
import { KRA_DOCUMENTS_REQUIRED_COUNT } from "features/ManageOfferings/modules/OfferingPageSections/modules/KRASubsection/modules/KRAContractUrlFooter/constants/KRAContractFooter.constants";
import {
  PER_TIER_KYC_COPY,
  PER_TIER_KYC_FIELD_KEYS,
  PER_TIER_KYC_METHOD,
  PER_TIER_KYC_MODE,
  PER_TIER_KYC_STYLE,
  PER_TIER_KYC_UPLOAD,
} from "./PerTierKyc.constants";
import { getModalCopyForContext, isCustomKycMode } from "./PerTierKyc.utils";
import { ExlyDropdown } from "common/form";

export const PerTierKycModal = withComponentLibraryTheme(
  ({
    open,
    recordTitle,
    context,
    offering,
    draft,
    errors = {},
    saving = false,
    loadingOffering = false,
    uploadBasePath = "",
    onModeChange,
    onMethodChange,
    onEnabledChange,
    onPlacementChange,
    onContractUrlChange,
    onWorkflowChange,
    onContractFile,
    onContractRemove,
    onClose,
    onSave,
  }) => {
    const copy = getModalCopyForContext(context);
    const readOnly = !isCustomKycMode(draft?.[PER_TIER_KYC_FIELD_KEYS.MODE]);
    const methodValue = readOnly
      ? offering?.[PER_TIER_KYC_FIELD_KEYS.KYC_TYPE] ?? null
      : draft?.[PER_TIER_KYC_FIELD_KEYS.KYC_TYPE] ?? null;
    const methodOptions = KYC_TYPE_OPTIONS.map((option) => ({
      ...option,
      disabled:
        readOnly ||
        (option.value === PER_TIER_KYC_METHOD.KRA && offering?.kraDisabled),
    }));
    const isKraSelected = methodValue === PER_TIER_KYC_METHOD.KRA;
    const isEsignSelected = methodValue === PER_TIER_KYC_METHOD.ESIGN;
    const offeringEnabled = isKraSelected
      ? Boolean(offering?.[PER_TIER_KYC_FIELD_KEYS.IS_DIGIO_KYC_ENABLED])
      : Boolean(offering?.[PER_TIER_KYC_FIELD_KEYS.IS_ESIGN_ENABLED]);
    const toggleValue = readOnly
      ? offeringEnabled
      : draft?.[PER_TIER_KYC_FIELD_KEYS.ENABLED] !== false;
    const isMethodSelected =
      methodValue === PER_TIER_KYC_METHOD.ESIGN || isKraSelected;
    const enableToggleLabel = isKraSelected
      ? PER_TIER_KYC_COPY.ENABLE_TOGGLE_KRA
      : PER_TIER_KYC_COPY.ENABLE_TOGGLE_AADHAAR;
    const modeOptions = [
      {
        id: PER_TIER_KYC_MODE.INHERIT,
        label: PER_TIER_KYC_COPY.MODE_INHERIT,
        value: PER_TIER_KYC_MODE.INHERIT,
      },
      {
        id: PER_TIER_KYC_MODE.CUSTOM,
        label: copy.customOption,
        value: PER_TIER_KYC_MODE.CUSTOM,
      },
    ];
    const handleKraRequestClick = () => {
      navigateTo(app_route_keys.FEATURES, {
        queryParams: { section_key: FEATURE_SECTION_KEY },
      });
    };
    const contractValue = readOnly
      ? offering?.[PER_TIER_KYC_FIELD_KEYS.CONTRACT_PDF_URL] ?? ""
      : draft?.[PER_TIER_KYC_FIELD_KEYS.CONTRACT_PDF_URL] ?? "";
    const kraDocs = readOnly
      ? getUploadedFiles(offering?.[PER_TIER_KYC_FIELD_KEYS.KRA_CONTRACT_URL])
      : getUploadedFiles(draft?.[PER_TIER_KYC_FIELD_KEYS.KRA_CONTRACT_URL]);
    const workflowValue = readOnly
      ? offering?.[PER_TIER_KYC_FIELD_KEYS.KYC_WORKFLOW] ?? ""
      : draft?.[PER_TIER_KYC_FIELD_KEYS.KYC_WORKFLOW] ?? "";
    // Enable toggle appears only once the required documents exist —
    // esign needs a valid contract URL, KRA needs both documents.
    // Inherit (read-only) mode keeps today's display behavior.
    const docsReady = isEsignSelected
      ? validURLV2(contractValue)
      : isKraSelected
      ? kraDocs.length >= KRA_DOCUMENTS_REQUIRED_COUNT
      : false;
    const showEnableToggle = isMethodSelected && (readOnly || docsReady);
    const placementValue = readOnly
      ? offering?.[PER_TIER_KYC_FIELD_KEYS.ESIGN_PLACEMENT] ??
        ESIGN_PLACEMENT.BEFORE_BOOKING
      : draft?.[PER_TIER_KYC_FIELD_KEYS.ESIGN_PLACEMENT] ??
        ESIGN_PLACEMENT.BEFORE_BOOKING;
    const placementOptions = ESIGN_PLACEMENT_OPTIONS.map((option) => ({
      ...option,
      disabled: readOnly,
    }));
    // Placement applies to enabled e-signing only — same gating as the
    // toggle, plus the toggle itself being on.
    const showPlacement =
      isEsignSelected && toggleValue && (readOnly || docsReady);
    return (
      <ExlyModal
        open={open}
        onClose={onClose}
        title={`${PER_TIER_KYC_COPY.TITLE_PREFIX} — ${recordTitle}`}
        desktopModalWidth={PER_TIER_KYC_STYLE.MODAL_WIDTH}
        primaryBtnText={PER_TIER_KYC_COPY.SAVE}
        onPrimaryBtnClick={onSave}
        primaryBtnProps={{ disabled: saving }}
        secondaryBtnText={PER_TIER_KYC_COPY.CANCEL}
        onSecondaryBtnClick={onClose}
        showSecondaryBtn
      >
        <div className="tw-flex tw-flex-col tw-gap-4 tw-py-4">
          <div className="tw-text-sm tw-text-gray-500">{copy.subtitle}</div>

          <div>
            <div className="tw-text-[14px] tw-font-medium tw-tracking-[0.28px] tw-text-[#000000D9] tw-mb-[10px]">
              {PER_TIER_KYC_COPY.MODE_LABEL}
            </div>
            <ExlyDropdown
              style={{ minHeight: 40 }}
              input={{
                value: draft?.[PER_TIER_KYC_FIELD_KEYS.MODE],
                onChange: (value) => onModeChange?.(value),
                name: PER_TIER_KYC_FIELD_KEYS.MODE_INPUT_NAME,
              }}
              fieldLabel={null}
              options={modeOptions}
              placeholder={PER_TIER_KYC_COPY.MODE_LABEL}
              fullWidth
              formControlClassName="tw-w-full"
            />
            {readOnly && (
              <div className="tw-text-sm tw-text-gray-500 tw-mt-2">
                {PER_TIER_KYC_COPY.INHERIT_HELPER}
              </div>
            )}
          </div>

          <div className="tw-rounded-lg tw-bg-gray-100 tw-px-5 tw-py-4 tw-flex tw-flex-col tw-gap-4">
            {loadingOffering ? (
              <div className="tw-flex tw-items-center tw-justify-center tw-py-2 !tw-h-[80px]">
                <ExlyLoader showText={false} />
              </div>
            ) : (
              <>
                <div className="tw-text-[12px] tw-font-medium tw-tracking-[0.24px] tw-text-gray-500">
                  {PER_TIER_KYC_COPY.METHOD_SECTION_TITLE}
                </div>
                <RadioGroup
                  options={methodOptions}
                  selected={methodOptions.find(
                    ({ value }) => value === methodValue
                  )}
                  onChange={({ value }) => onMethodChange?.(value)}
                  formControlLabelClassName={classnames(
                    "!tw-whitespace-nowrap",
                    "tw-text-sm md:tw-text-base"
                  )}
                />
                {errors[PER_TIER_KYC_FIELD_KEYS.KYC_TYPE] && (
                  <div className="tw-text-sm tw-text-[#BF2600]">
                    {errors[PER_TIER_KYC_FIELD_KEYS.KYC_TYPE]}
                  </div>
                )}
                {offering?.kraDisabled && (
                  <div
                    onClick={handleKraRequestClick}
                    className="tw-flex tw-border tw-border-[rgb(15,97,156)] tw-text-[rgb(15,97,156)] tw-bg-[#eef8ff] tw-border-solid tw-h-fit tw-items-center tw-justify-center tw-rounded tw-p-[6px_12px] tw-cursor-pointer tw-text-[12px] tw-font-medium tw-leading-normal"
                  >
                    {PER_TIER_KYC_COPY.KRA_REQUEST_BANNER}
                  </div>
                )}
                {isEsignSelected && (
                  <>
                    {validURLV2(contractValue) ? (
                      <div
                        onClick={() => window.open(contractValue, "_blank")}
                        className="tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-w-full tw-rounded-lg tw-bg-white tw-px-5 tw-py-3 tw-mb-2"
                      >
                        <div className="tw-flex tw-items-center tw-gap-4">
                          <ExlyImage
                            className="tw-w-5 tw-object-contain"
                            src={getS3AssetCDNUrl(
                              "assets/vectors/dashboard/red_pdf.png"
                            )}
                          />
                          <span className="tw-text-sm -tw-ml-1 tw-font-medium tw-text-gray-900">
                            {PER_TIER_KYC_COPY.CONTRACT_FILE_LABEL}
                          </span>
                        </div>
                        {!readOnly && (
                          <div
                            onClick={(event) => {
                              event.stopPropagation();
                              if (onContractRemove) onContractRemove();
                            }}
                            className="tw-bg-[#787878] tw-rounded-full tw-w-5 tw-h-5 tw-aspect-square tw-flex tw-items-center tw-justify-center"
                          >
                            <Close
                              fontSize="small"
                              className="tw-text-white !tw-w-4"
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        <div>
                          <div className="tw-text-[14px] tw-font-medium tw-text-gray-900 tw-mb-[10px]">
                            {PER_TIER_KYC_COPY.CONTRACT_URL_LABEL}
                          </div>
                          <TextField
                            value={contractValue}
                            onChange={(event) =>
                              onContractUrlChange?.(event.target.value)
                            }
                            placeholder={
                              PER_TIER_KYC_COPY.CONTRACT_URL_PLACEHOLDER
                            }
                            variant="outlined"
                            size="small"
                            fullWidth
                            disabled={readOnly}
                            error={Boolean(
                              errors[PER_TIER_KYC_FIELD_KEYS.CONTRACT_PDF_URL]
                            )}
                            helperText={
                              errors[
                                PER_TIER_KYC_FIELD_KEYS.CONTRACT_PDF_URL
                              ] ?? ""
                            }
                          />
                        </div>
                        {!readOnly && (
                          <>
                            <div className="tw-flex tw-items-center tw-w-full tw-gap-4 tw-py-2">
                              <div className="tw-flex-1 tw-h-px tw-bg-gray-300" />
                              <span className="tw-text-gray-500 tw-text-sm tw-font-medium">
                                {PER_TIER_KYC_COPY.OR_DIVIDER}
                              </span>
                              <div className="tw-flex-1 tw-h-px tw-bg-gray-300" />
                            </div>
                            <div className="tw-text-[14px] tw-text-gray-500">
                              {PER_TIER_KYC_COPY.CONTRACT_UPLOAD_LABEL}
                            </div>
                            <UploadFileButton
                              btnText={PER_TIER_KYC_COPY.CONTRACT_UPLOAD_BUTTON}
                              startIcon={AddCircleOutline}
                              accept={PER_TIER_KYC_UPLOAD.ACCEPT}
                              onSuccess={(fileLocation) =>
                                onContractFile?.(fileLocation)
                              }
                              className="!tw-text-[14px] tw-w-full"
                              baseFilePath={uploadBasePath}
                            />
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
                {isKraSelected && (
                  <div className="tw-flex tw-flex-col tw-gap-2">
                    {kraDocs.map((fileUrl, index) => (
                      <div
                        key={`${fileUrl}-${index}`}
                        onClick={() => window.open(fileUrl, "_blank")}
                        className="tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-w-full tw-rounded-lg tw-bg-white tw-px-5 tw-py-3 tw-mb-2"
                      >
                        <div className="tw-flex tw-items-center tw-gap-4">
                          <ExlyImage
                            className="tw-w-5 tw-object-contain"
                            src={getS3AssetCDNUrl(
                              "assets/vectors/dashboard/red_pdf.png"
                            )}
                          />
                          <span className="tw-text-sm -tw-ml-1 tw-font-medium tw-text-gray-900">
                            {PER_TIER_KYC_COPY.KRA_UPLOAD_BUTTONS[index]
                              ?.fileLabel ??
                              `${PER_TIER_KYC_COPY.CONTRACT_FILE_LABEL} ${
                                index + 1
                              }`}
                          </span>
                        </div>
                        {!readOnly && (
                          <div
                            onClick={(event) => {
                              event.stopPropagation();
                              if (onContractRemove) onContractRemove(index);
                            }}
                            className="tw-bg-[#787878] tw-rounded-full tw-w-5 tw-h-5 tw-aspect-square tw-flex tw-items-center tw-justify-center"
                          >
                            <Close
                              fontSize="small"
                              className="tw-text-white !tw-w-4"
                            />
                          </div>
                        )}
                      </div>
                    ))}
                    {!readOnly &&
                      kraDocs.length < KRA_DOCUMENTS_REQUIRED_COUNT &&
                      PER_TIER_KYC_COPY.KRA_UPLOAD_BUTTONS.slice(
                        kraDocs.length
                      ).map((uploadConfig) => (
                        <UploadFileButton
                          key={uploadConfig.btnText}
                          btnText={uploadConfig.btnText}
                          startIcon={AddCircleOutline}
                          accept={PER_TIER_KYC_UPLOAD.ACCEPT}
                          onSuccess={(fileLocation) =>
                            onContractFile?.(fileLocation, kraDocs.length)
                          }
                          className="!tw-text-[14px] tw-w-full"
                          baseFilePath={uploadBasePath}
                        />
                      ))}
                    {errors[PER_TIER_KYC_FIELD_KEYS.KRA_CONTRACT_URL] && (
                      <div className="tw-text-sm tw-text-[#BF2600]">
                        {errors[PER_TIER_KYC_FIELD_KEYS.KRA_CONTRACT_URL]}
                      </div>
                    )}
                    <div>
                      <div className="tw-text-[14px] tw-font-medium tw-text-gray-900 tw-mb-[10px]">
                        {PER_TIER_KYC_COPY.WORKFLOW_LABEL}
                      </div>
                      <TextField
                        value={workflowValue}
                        onChange={(event) =>
                          onWorkflowChange?.(event.target.value)
                        }
                        placeholder={PER_TIER_KYC_COPY.WORKFLOW_PLACEHOLDER}
                        variant="outlined"
                        size="small"
                        fullWidth
                        disabled={readOnly}
                      />
                      <div className="tw-text-[12px] tw-text-gray-500 tw-mt-1">
                        {PER_TIER_KYC_COPY.WORKFLOW_HELPER}
                      </div>
                    </div>
                  </div>
                )}
                {showEnableToggle && (
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <div
                      className={
                        toggleValue
                          ? ""
                          : "tw-rounded-full tw-border tw-border-gray-400 tw-p-[1px]"
                      }
                    >
                      <SectionUnlockingSwitch
                        value={toggleValue}
                        onChange={onEnabledChange}
                        disabled={readOnly}
                      />
                    </div>
                    <span className="tw-text-sm tw-font-medium tw-text-gray-900">
                      {enableToggleLabel}
                    </span>
                  </div>
                )}
                {showPlacement && (
                  <div>
                    <div className="tw-text-[14px] tw-font-medium tw-text-gray-900 tw-mb-[10px]">
                      {PER_TIER_KYC_COPY.PLACEMENT_TITLE}
                    </div>
                    <div className="tw-text-sm tw-text-gray-500 tw-mb-[10px]">
                      {PER_TIER_KYC_COPY.PLACEMENT_DESC}
                    </div>
                    <RadioGroup
                      options={placementOptions}
                      selected={placementOptions.find(
                        ({ value }) => value === placementValue
                      )}
                      onChange={({ value }) => onPlacementChange?.(value)}
                      formControlLabelClassName={classnames(
                        "!tw-whitespace-nowrap",
                        "tw-text-sm md:tw-text-base"
                      )}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </ExlyModal>
    );
  }
);
